/**
 * LanguagePreferenceModal — first-login language picker (Feb 2026 iter 265).
 *
 * DESIGN GOAL: this is the very first screen a NEW client sees when they log
 * into the client area. It MUST be understandable regardless of the language
 * the client speaks. Prior versions showed the title/subtitle/save button in
 * ONE language (guessed from `LanguageContext`, which was pre-seeded from
 * the browser locale). If the guess was wrong — e.g. a US client whose
 * browser locale was pt-BR — the client saw a modal in a language they did
 * not understand and closed it without saving.
 *
 * Fix (iter 265): show the title, subtitle and save button in ALL 3
 * languages simultaneously. The modal is now self-evident: any client
 * finds their language and clicks. No pre-detection needed.
 *
 * The modal is blocking (no X, no ESC, no outside-click dismiss) so the
 * client can't accidentally leave without saving. The `preferred_language`
 * on the server is the single source of truth.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';
import { useClientAuth } from '../context/ClientAuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const LANGS = [
  { code: 'pt', flag: '🇧🇷', label_native: 'Português', label_hint: 'Brasil' },
  { code: 'en', flag: '🇺🇸', label_native: 'English', label_hint: 'USA' },
  { code: 'es', flag: '🇪🇸', label_native: 'Español', label_hint: 'España' },
];

// Trilingual copy used at the top of the modal (stacked).
const TITLE_LINES = [
  { lang: 'pt', text: 'Qual idioma você prefere?' },
  { lang: 'en', text: 'Which language do you prefer?' },
  { lang: 'es', text: '¿Qué idioma prefieres?' },
];
const SUBTITLE_LINES = [
  { lang: 'pt', text: 'Usaremos em e-mails, contratos e área do cliente.' },
  { lang: 'en', text: "We'll use it in emails, contracts and the client area." },
  { lang: 'es', text: 'Lo usaremos en correos, contratos y área del cliente.' },
];
const SAVE_TRI = 'Salvar · Save · Guardar';

const LanguagePreferenceModal = () => {
  const { language, changeLanguage } = useLanguage();
  const { client, token, isAuthenticated } = useClientAuth();

  // Modal shows when profile has no valid preferred_language yet.
  // DB is the source of truth — we do NOT gate on localStorage.
  const shouldShow = useMemo(() => {
    if (!isAuthenticated || !client) return false;
    const pl = (client.preferred_language || '').toLowerCase();
    return !['pt', 'en', 'es'].includes(pl);
  }, [isAuthenticated, client]);

  // Sync LanguageContext to the client's stored preference ONLY when the
  // visitor hasn't explicitly picked a language in this session. Prevents
  // the common bug "US visitor selected EN → clicked My Account → UI
  // switched to PT because their account's preferred_language=pt".
  useEffect(() => {
    if (!isAuthenticated || !client) return;
    const pl = (client.preferred_language || '').toLowerCase();
    let userExplicit = false;
    try { userExplicit = localStorage.getItem('demarre-language-explicit') === '1'; } catch { /* ignore */ }
    if (['pt', 'en', 'es'].includes(pl) && pl !== language && !userExplicit) {
      changeLanguage(pl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, client?.preferred_language]);

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (shouldShow) {
      setOpen(true);
      setSelected(null);  // Nothing pre-selected — force explicit choice.
    } else {
      setOpen(false);
    }
  }, [shouldShow]);

  const handleSave = async () => {
    if (saving || !selected) return;
    setSaving(true);
    try {
      await fetch(`${API}/api/client/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ preferred_language: selected }),
      });
      changeLanguage(selected);
      try {
        const saved = JSON.parse(localStorage.getItem('client-data') || '{}');
        saved.preferred_language = selected;
        localStorage.setItem('client-data', JSON.stringify(saved));
      } catch { /* ignore */ }
      setOpen(false);
      setTimeout(() => window.location.reload(), 200);
    } catch (e) {
      // If save fails, keep the modal so the user can retry — the DB is
      // still without a valid language.
    } finally {
      setSaving(false);
    }
  };

  if (!shouldShow) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="max-w-md"
        data-testid="client-language-modal"
        hideCloseButton
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        {/* Accessibility (iter 265): Radix requires a DialogTitle + DialogDescription
            for screen readers. We hide them visually since the visible title/subtitle
            below is already stacked in 3 languages. */}
        <DialogTitle className="sr-only">
          Qual idioma você prefere? · Which language do you prefer? · ¿Qué idioma prefieres?
        </DialogTitle>
        <DialogDescription className="sr-only">
          Usaremos em e-mails, contratos e área do cliente. We will use it in emails, contracts and the client area. Lo usaremos en correos, contratos y área del cliente.
        </DialogDescription>

        {/* Trilingual title block — no DialogHeader so the copy stacks cleanly. */}
        <div className="text-center pt-1 pb-1">
          <div className="text-3xl mb-2" aria-hidden="true">🌐</div>
          <div className="space-y-1">
            {TITLE_LINES.map((t) => (
              <div
                key={t.lang}
                className="text-base sm:text-lg font-semibold text-gray-900 leading-snug"
                lang={t.lang}
                data-testid={`client-language-title-${t.lang}`}
              >
                {t.text}
              </div>
            ))}
          </div>
          <div className="mt-2 space-y-0.5">
            {SUBTITLE_LINES.map((s) => (
              <div
                key={s.lang}
                className="text-xs text-gray-500 leading-snug"
                lang={s.lang}
              >
                {s.text}
              </div>
            ))}
          </div>
        </div>

        <div className="grid gap-2 mt-2">
          {LANGS.map((l) => (
            <button
              key={l.code}
              type="button"
              onClick={() => setSelected(l.code)}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg border text-left transition ${
                selected === l.code
                  ? 'border-demarre-700 bg-demarre-50 ring-2 ring-demarre-200'
                  : 'border-gray-200 hover:bg-gray-50'
              }`}
              data-testid={`client-language-opt-${l.code}`}
              lang={l.code}
            >
              <span className="text-3xl" aria-hidden="true">{l.flag}</span>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-gray-900 text-base">{l.label_native}</div>
                <div className="text-xs text-gray-500">{l.label_hint}</div>
              </div>
              {selected === l.code && (
                <span className="text-demarre-700 text-2xl leading-none" aria-hidden="true">✓</span>
              )}
            </button>
          ))}
        </div>

        <Button
          onClick={handleSave}
          disabled={saving || !selected}
          className="mt-3 bg-demarre-700 hover:bg-demarre-800 disabled:opacity-50"
          data-testid="client-language-save"
        >
          {saving ? '…' : SAVE_TRI}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default LanguagePreferenceModal;
